import { MedplumClient, resolveId } from "@medplum/core";
import {
  Bundle,
  Encounter,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  Reference,
} from "@medplum/fhirtypes";

import isEqual from "lodash/isEqual";

import {
  AMENDMENT_HISTORY_QUESTIONNAIRE_RESPONSE_STATUSES,
  isAmendmentHistoryQuestionnaireResponseStatus,
  QUESTIONNAIRE_RESPONSE_STATUS_AMENDED,
  QUESTIONNAIRE_RESPONSE_STATUS_COMPLETED,
} from "@/encounters/constants/questionnaire-response-amendment";
import {
  compareQuestionnaireResponsesByVersionIdDesc,
  getQuestionnaireResponseChangeInstant,
} from "@/encounters/utils/questionnaire-response-instant";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { joinReferenceString } from "@/utils/medplum";
import { collectResourcesFromPaginatedBundle } from "@/utils/pagination";

import {
  getEncounterAnswersQuestionnaireResponse,
  getEncounterQuestionnaireResponse,
} from "../utils/questionnaire-response";
import { EncounterAnswersFormType } from "../zod-schemas/encounter-answers";
import { EncounterFormType } from "../zod-schemas/encounters";

import { fetchEncounterQuestionnaire } from "./questionnaire";

/** Newest amendment/completed versions first; stable for version diff and amendment cards. */
export function sortQuestionnaireResponsesNewestFirst(
  questionnaireResponses: QuestionnaireResponse[],
): QuestionnaireResponse[] {
  return [...questionnaireResponses].sort(compareQuestionnaireResponsesByVersionIdDesc);
}

export interface BuildAmendmentHistoryOptions {
  /** When true, only `amended` versions are returned (amendment card / history list). */
  amendedOnly?: boolean;
  /**
   * When true with `amendedOnly`, returns only `amended` versions and does not fall back to
   * routine `completed` QuestionnaireResponse history saves (legacy slice excludes oldest
   * completed only when this is false).
   */
  strictAmendedOnly?: boolean;
}

export type FetchAmendmentHistoryOptions = BuildAmendmentHistoryOptions;

/**
 * Reads all QuestionnaireResponse history entries, following pagination links when present.
 */
export async function readQuestionnaireResponseHistory(
  medplum: MedplumClient,
  questionnaireResponseId: string,
): Promise<QuestionnaireResponse[]> {
  const initialBundle: Bundle<QuestionnaireResponse> = await medplum.readHistory(
    "QuestionnaireResponse",
    questionnaireResponseId,
  );

  return collectResourcesFromPaginatedBundle(medplum, initialBundle, "QuestionnaireResponse");
}

function dedupeQuestionnaireResponseHistoryByVersionId(
  resources: QuestionnaireResponse[],
): QuestionnaireResponse[] {
  const seenVersionIds = new Set<string>();
  return resources.filter((resource) => {
    const versionId = resource.meta?.versionId;
    if (!versionId) {
      return true;
    }
    if (seenVersionIds.has(versionId)) {
      return false;
    }
    seenVersionIds.add(versionId);
    return true;
  });
}

/**
 * The encounter QuestionnaireResponse versions that count as amendments, newest first.
 *
 * Every version is kept, even when its answers are the same as the version before it. An amendment
 * can change only resources stored outside the QuestionnaireResponse. The procedures tab saves
 * Condition and Procedure resources, and physical exams and files save their own resources. The
 * amendment can also reuse the previous amendment's reason, so two versions with the same `item`
 * are still two amendments. Removing one of them also removes its row from the amendment card and
 * its version id, which the amendment view and the procedures amendment window are read by.
 *
 * Nested section QuestionnaireResponses are read through
 * {@link fetchAmendmentHistoryByQuestionnaireUrl}, which still collapses repeated answers with
 * {@link collapseQuestionnaireResponseStatusOnlyVersions}.
 */
export function buildAmendmentHistoryFromResources(
  resources: QuestionnaireResponse[],
  options: BuildAmendmentHistoryOptions = {},
): QuestionnaireResponse[] {
  const filtered = resources.filter((resource) =>
    isAmendmentHistoryQuestionnaireResponseStatus(resource.status),
  );

  // Sort by change instant (lastUpdated / authored) so the newest version is first.
  // When timestamps are equal (e.g. test environments with frozen clocks), stable sort
  // preserves the readHistory bundle order, which FHIR servers return newest-first.
  const sortedNewestFirst = sortQuestionnaireResponsesByChangeInstantDesc(
    dedupeQuestionnaireResponseHistoryByVersionId(filtered),
  );

  if (options.amendedOnly) {
    if (options.strictAmendedOnly) {
      return sortQuestionnaireResponsesByChangeInstantDesc(
        sortedNewestFirst.filter(
          (resource) => resource.status === QUESTIONNAIRE_RESPONSE_STATUS_AMENDED,
        ),
      );
    }

    // Exclude only the original sign-off (oldest completed), matching legacy slice(0, -1). A
    // completed procedures-only amendment is kept alongside amended versions rather than dropped:
    // switching entirely to amended-only versions whenever any exist left a completed amendment with
    // no history row and no reachable version id.
    const oldestCompletedIndex = sortedNewestFirst.findLastIndex(
      (resource) => resource.status === QUESTIONNAIRE_RESPONSE_STATUS_COMPLETED,
    );
    if (oldestCompletedIndex === -1) {
      return sortedNewestFirst;
    }
    return sortedNewestFirst.filter((_, index) => index !== oldestCompletedIndex);
  }

  return sortedNewestFirst;
}

/**
 * Newest-first QuestionnaireResponse versions for version diff pairing.
 *
 * Unlike {@link buildAmendmentHistoryFromResources} without `amendedOnly`, this keeps
 * `in-progress` saves so the version immediately before an `amended` entry is available as
 * `previousVersion` (Sign & Lock often leaves the pre-amend save in-progress).
 */
export function buildQuestionnaireResponseVersionHistoryForDiff(
  resources: QuestionnaireResponse[],
): QuestionnaireResponse[] {
  const eligibleStatuses = new Set<QuestionnaireResponse["status"] | undefined>([
    ...AMENDMENT_HISTORY_QUESTIONNAIRE_RESPONSE_STATUSES,
    "in-progress",
    undefined,
  ]);

  const filtered = resources.filter((resource) => eligibleStatuses.has(resource.status));

  return sortQuestionnaireResponsesByChangeInstantDesc(
    dedupeQuestionnaireResponseHistoryByVersionId(filtered),
  );
}

/** Newest-first by materialization time (preferred for amend baseline pairing). */
export function sortQuestionnaireResponsesByChangeInstantDesc(
  questionnaireResponses: QuestionnaireResponse[],
): QuestionnaireResponse[] {
  return [...questionnaireResponses].sort((first, second) => {
    const firstInstant = getQuestionnaireResponseChangeInstant(first) ?? 0;
    const secondInstant = getQuestionnaireResponseChangeInstant(second) ?? 0;
    return secondInstant - firstInstant;
  });
}

/**
 * Removes adjacent history entries whose questionnaire answers are unchanged.
 *
 * A nested section can be saved twice during one Sign & Amend, once by the drawer and once by the
 * parent, which stores a new FHIR version with the same `item`. The section's diff would then
 * compare two identical snapshots and never show the values the amendment replaced. The raw server
 * history is not changed; this normalized list is only used to pick the snapshots a nested section
 * diffs.
 *
 * This is for nested section responses only. The encounter's own history keeps every version, see
 * {@link buildAmendmentHistoryFromResources}.
 */
export function collapseQuestionnaireResponseStatusOnlyVersions(
  questionnaireResponses: QuestionnaireResponse[],
): QuestionnaireResponse[] {
  return questionnaireResponses.filter((resource, index) => {
    if (index === 0) return true;
    return !isEqual(resource.item ?? [], questionnaireResponses[index - 1]?.item ?? []);
  });
}

export interface EncounterQuestionnaireVersionPairForDiff {
  versionHistory: QuestionnaireResponse[];
  currentVersion: QuestionnaireResponse;
  previousVersion?: QuestionnaireResponse;
  currentVersionIndex: number;
}

/**
 * Resolves the encounter QuestionnaireResponse pair shown on labor version diff.
 *
 * Uses full server history (not amendment-card filtering). Pairing prefers change-instant
 * ordering so the Sign & Lock baseline before the first `amended` save is found even when
 * `meta.versionId` ordering differs.
 */
export function resolveEncounterQuestionnaireVersionPairForDiff(
  historyResources: QuestionnaireResponse[],
  targetVersionId: string,
): EncounterQuestionnaireVersionPairForDiff | undefined {
  const versionHistory = sortQuestionnaireResponsesByChangeInstantDesc(
    dedupeQuestionnaireResponseHistoryByVersionId(historyResources),
  );

  const currentVersionIndex = versionHistory.findIndex(
    (entry) => entry.meta?.versionId === targetVersionId,
  );
  if (currentVersionIndex === -1) {
    return undefined;
  }

  const currentVersion = versionHistory[currentVersionIndex];
  const priorVersions = versionHistory.slice(currentVersionIndex + 1);
  const signAndLockBaseline = priorVersions.find(
    (entry) =>
      entry.meta?.versionId !== currentVersion.meta?.versionId &&
      (entry.status === "completed" || entry.status === "in-progress"),
  );
  const previousVersion = signAndLockBaseline ?? priorVersions[0];

  return {
    versionHistory,
    currentVersion,
    previousVersion,
    currentVersionIndex,
  };
}

type CreateEncounterQuestionnaireResponseParams = {
  form: EncounterFormType;
  status: QuestionnaireResponse["status"];
  clientId: string;
  practitionerReference: Reference<Practitioner>;
  medplum: MedplumClient;
  /** Passed in so this module never reads `import.meta.env`, which is empty in CJS bot bundles. */
  encounterBotUrl: string;
  encounterId?: string;
  /** Canonical newborn reference for a standalone Newborn IPP/Postpartum creation. */
  newbornPatientReference?: Reference<Patient>;
};

export async function createOrUpdateEncounterResources({
  form,
  status,
  clientId,
  practitionerReference,
  medplum,
  encounterBotUrl,
  encounterId,
  newbornPatientReference,
}: CreateEncounterQuestionnaireResponseParams) {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.Encounter,
  });

  if (!questionnaire) {
    throw new Error("Questionnaire not found");
  }

  const { id: questionnaireId } = questionnaire;

  const questionnaireResponseToCreate: QuestionnaireResponse = getEncounterQuestionnaireResponse({
    form,
    clientId,
    practitionerReference,
    status,
    encounterId,
    questionnaireId,
    newbornPatientReference,
  });

  const questionnaireResponse = await medplum.createResource(questionnaireResponseToCreate);

  const encounterResults = await medplum.executeBot(encounterBotUrl, {
    ...questionnaireResponse,
  });

  return encounterResults;
}

type CreateEncounterAnswerQuestionnaireResponseParams = {
  form: EncounterAnswersFormType;
  status: QuestionnaireResponse["status"];
  clientId: string;
  practitionerReference: Reference<Practitioner>;
  questionnaire: Questionnaire;
  medplum: MedplumClient;
  encounterId: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export async function createEncounterAnswerQuestionnaireResponse({
  form,
  status,
  clientId,
  practitionerReference,
  questionnaire,
  medplum,
  encounterId,
  questionnaireResponse,
}: CreateEncounterAnswerQuestionnaireResponseParams) {
  const questionnaireResponseData = getEncounterAnswersQuestionnaireResponse({
    clientId,
    form,
    practitionerReference,
    questionnaire,
    status,
    encounterId,
    questionnaireResponse,
  });

  const updatedQuestionnaireResponse = await medplum.upsertResource(
    { ...questionnaireResponseData, authored: new Date().toISOString() },
    {
      encounter: joinReferenceString("Encounter", encounterId),
      questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
      subject: joinReferenceString("Patient", clientId),
    },
  );

  return updatedQuestionnaireResponse;
}

type FetchEncounterQuestionnaireResponseParams = {
  encounterId: string;
  questionnaireId: string;
  clientId: string;
  medplum: MedplumClient;
};

export const fetchEncounterQuestionnaireResponse = async ({
  encounterId,
  questionnaireId,
  clientId,
  medplum,
}: FetchEncounterQuestionnaireResponseParams) => {
  const questionnaireResponse = await medplum.searchResources("QuestionnaireResponse", {
    encounter: joinReferenceString("Encounter", encounterId),
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    subject: joinReferenceString("Patient", clientId),
    _sort: "-_lastUpdated",
    _count: 1,
  });

  return questionnaireResponse[0] ?? null;
};

export const fetchAmendmentHistory = async (
  medplum: MedplumClient,
  encounter?: Encounter,
  options: BuildAmendmentHistoryOptions = {},
) => {
  if (!encounter) return;

  const questionnaire = await fetchEncounterQuestionnaire(medplum, encounter, { _elements: "url" });
  if (!questionnaire) return;

  const patientId = resolveId(encounter.subject);
  if (!patientId) return;

  const questionnaireResponse = await fetchEncounterQuestionnaireResponse({
    encounterId: encounter.id!,
    questionnaireId: questionnaire.id!,
    clientId: patientId,
    medplum,
  });

  if (!questionnaireResponse?.id) return;

  const historyResources = await readQuestionnaireResponseHistory(
    medplum,
    questionnaireResponse.id,
  );
  if (historyResources.length === 0) return;

  return buildAmendmentHistoryFromResources(historyResources, options);
};

/** Loads encounter QuestionnaireResponse version history for labor/encounter version diff routes. */
export async function fetchEncounterQuestionnaireResponseVersionHistoryForDiff(
  medplum: MedplumClient,
  encounter?: Encounter,
): Promise<QuestionnaireResponse[] | undefined> {
  if (!encounter) {
    return undefined;
  }

  const questionnaire = await fetchEncounterQuestionnaire(medplum, encounter, { _elements: "url" });
  if (!questionnaire) {
    return undefined;
  }

  const patientId = resolveId(encounter.subject);
  if (!patientId) {
    return undefined;
  }

  const questionnaireResponse = await fetchEncounterQuestionnaireResponse({
    encounterId: encounter.id!,
    questionnaireId: questionnaire.id!,
    clientId: patientId,
    medplum,
  });

  if (!questionnaireResponse?.id) {
    return undefined;
  }

  const historyResources = await readQuestionnaireResponseHistory(
    medplum,
    questionnaireResponse.id,
  );
  if (historyResources.length === 0) {
    return undefined;
  }

  return buildQuestionnaireResponseVersionHistoryForDiff(historyResources);
}

/** Loads encounter QR history and resolves current/previous versions for version diff. */
export async function fetchEncounterQuestionnaireResponseVersionPairForDiff(
  medplum: MedplumClient,
  encounter: Encounter | undefined,
  targetVersionId: string,
): Promise<EncounterQuestionnaireVersionPairForDiff | undefined> {
  if (!encounter) {
    return undefined;
  }

  const questionnaire = await fetchEncounterQuestionnaire(medplum, encounter, { _elements: "url" });
  if (!questionnaire) {
    return undefined;
  }

  const patientId = resolveId(encounter.subject);
  if (!patientId) {
    return undefined;
  }

  const questionnaireResponse = await fetchEncounterQuestionnaireResponse({
    encounterId: encounter.id!,
    questionnaireId: questionnaire.id!,
    clientId: patientId,
    medplum,
  });

  if (!questionnaireResponse?.id) {
    return undefined;
  }

  const historyResources = await readQuestionnaireResponseHistory(
    medplum,
    questionnaireResponse.id,
  );
  if (historyResources.length === 0) {
    return undefined;
  }

  return resolveEncounterQuestionnaireVersionPairForDiff(historyResources, targetVersionId);
}

export const fetchAmendmentHistoryByQuestionnaireUrl = async (
  medplum: MedplumClient,
  questionnaireUrl: string,
  encounterId: string,
  removeLastItem = false,
): Promise<[QuestionnaireResponse[] | undefined, string | undefined]> => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: questionnaireUrl,
  });

  if (!questionnaire) return [undefined, undefined];

  const questionnaireResponse = await medplum.searchOne("QuestionnaireResponse", {
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    encounter: joinReferenceString("Encounter", encounterId),
    _sort: "-_lastUpdated",
  });

  if (!questionnaireResponse || !questionnaireResponse.id) return [undefined, undefined];

  const historyResources = await readQuestionnaireResponseHistory(
    medplum,
    questionnaireResponse.id,
  );
  if (historyResources.length === 0) return [undefined, undefined];

  const amendmentHistory = buildAmendmentHistoryFromResources(historyResources, {
    amendedOnly: removeLastItem,
  });

  return [collapseQuestionnaireResponseStatusOnlyVersions(amendmentHistory), questionnaire.title];
};

/**
 * Every version of one QuestionnaireResponse, newest first, read across all history pages.
 *
 * @param medplum - Medplum client instance
 * @param questionnaireResponseId - The QuestionnaireResponse whose history is read
 * @returns The versions in history order, or an empty list when no id is given
 */
export const fetchQuestionnaireResponseHistoryByQuestionnaireResponseId = async (
  medplum: MedplumClient,
  questionnaireResponseId?: string,
): Promise<QuestionnaireResponse[]> => {
  if (!questionnaireResponseId) return [];

  return readQuestionnaireResponseHistory(medplum, questionnaireResponseId);
};
